import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, Injector, Type } from '@angular/core';

/**
 * Simple modal service replacing angular2-modal.
 * Uses Bootstrap 3 modal CSS that's already included in the project.
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
  private backdrop: HTMLElement | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  alert(message: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const overlay = this.createOverlay();
      const modalHtml = `
        <div class="modal" style="display:block" tabindex="-1" role="dialog">
          <div class="modal-dialog" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <button type="button" class="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                <h4 class="modal-title">Information</h4>
              </div>
              <div class="modal-body">
                <p>${this.escapeHtml(message)}</p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-primary" data-dismiss="modal">OK</button>
              </div>
            </div>
          </div>
        </div>
      `;

      overlay.innerHTML = modalHtml;

      const closeBtn = overlay.querySelectorAll('[data-dismiss="modal"]');
      closeBtn.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.removeOverlay(overlay);
          resolve();
        });
      });
    });
  }

  confirm(message: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const overlay = this.createOverlay();
      const modalHtml = `
        <div class="modal" style="display:block" tabindex="-1" role="dialog">
          <div class="modal-dialog" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <button type="button" class="close" data-action="cancel" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                <h4 class="modal-title">Confirmation</h4>
              </div>
              <div class="modal-body">
                <p>${this.escapeHtml(message)}</p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-default" data-action="cancel">Annuler</button>
                <button type="button" class="btn btn-primary" data-action="confirm">OK</button>
              </div>
            </div>
          </div>
        </div>
      `;

      overlay.innerHTML = modalHtml;

      overlay.querySelectorAll('[data-action="cancel"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.removeOverlay(overlay);
          resolve(false);
        });
      });

      overlay.querySelectorAll('[data-action="confirm"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.removeOverlay(overlay);
          resolve(true);
        });
      });
    });
  }

  /**
   * Open a component as a modal dialog.
   * The component receives a `dialogRef` object with `close(result)` and `dismiss()` methods.
   * Returns a Promise that resolves with the result when the dialog is closed.
   */
  open<T>(component: Type<T>, context?: any): Promise<any> {
    return new Promise<any>((resolve) => {
      const overlay = this.createOverlay();

      // Create wrapper for the modal chrome
      const wrapper = document.createElement('div');
      wrapper.className = 'modal';
      wrapper.style.display = 'block';
      wrapper.setAttribute('tabindex', '-1');
      wrapper.setAttribute('role', 'dialog');

      const dialog = document.createElement('div');
      dialog.className = 'modal-dialog' + (context && context.size === 'lg' ? ' modal-lg' : '');
      dialog.setAttribute('role', 'document');

      const content = document.createElement('div');
      content.className = 'modal-content';

      // Host element for the Angular component
      const hostEl = document.createElement('div');
      content.appendChild(hostEl);
      dialog.appendChild(content);
      wrapper.appendChild(dialog);
      overlay.appendChild(wrapper);

      // Forward-declare componentRef so dialogRef close/dismiss can reference it
      let componentRef: ComponentRef<T>;

      const dialogRef: DialogRef = {
        context: context || {},
        close: (result?: any) => {
          this.appRef.detachView(componentRef.hostView);
          componentRef.destroy();
          this.removeOverlay(overlay);
          resolve(result);
        },
        dismiss: () => {
          this.appRef.detachView(componentRef.hostView);
          componentRef.destroy();
          this.removeOverlay(overlay);
          resolve(undefined);
        },
      } as DialogRef;

      const elementInjector = Injector.create({
        providers: [{ provide: DialogRef, useValue: dialogRef }],
        parent: this.injector,
      });

      componentRef = createComponent(component, {
        environmentInjector: this.injector,
        elementInjector,
        hostElement: hostEl,
      });

      // Set context on the component instance (some components read instance.context)
      const instance = componentRef.instance as any;
      if (instance !== null && instance !== undefined && context) {
        instance.context = context;
      }

      this.appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  private createOverlay(): HTMLElement {
    // Backdrop
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'modal-backdrop fade in';
    document.body.appendChild(this.backdrop);

    // Overlay container
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-container';
    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');
    return overlay;
  }

  private removeOverlay(overlay: HTMLElement): void {
    if (this.backdrop) {
      document.body.removeChild(this.backdrop);
      this.backdrop = null;
    }
    document.body.removeChild(overlay);
    document.body.classList.remove('modal-open');
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}

/**
 * Dialog reference injected into modal components.
 * Compatible with the old angular2-modal DialogRef API surface.
 * Abstract class so DI can use it as an injection token.
 */
export abstract class DialogRef<T = any> {
  abstract context: T;
  abstract close(result?: any): void;
  abstract dismiss(): void;
}
